<script setup>
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { computed } from "vue"

import { getDistanceFromLatLonInMiles } from '@/lib/geography_helper.js'
import ServiceProviderCardBase from '@/views/service_providers/components/service_provider_cards/service_provider_card_base.vue'
import DealerType from '@/components/service_provider/dealer_type.vue'
import Map from '@/components/map/map.vue'

const props = defineProps({
    serviceProvider: {
        type: Object,
        required: true
    },
    compact: {
        type: Boolean,
        default: false
    }
})

// I18n
const { t } = useI18n()

// Route
const route = useRoute()

// Computed classes
const pillClasses = computed(() => {
    const tier = props.serviceProvider.sponsored_scope_tier
    return {
        'pill-gold': tier === 2,
        'pill-bronze': tier === 1,
        'pill-free': !tier,
        'pill-compact': props.compact
    }
})

const titleClasses = computed(() => {
    const tier = props.serviceProvider.sponsored_scope_tier
    return {
        'text-xl font-bold mb-3': tier === 2,
        'text-lg font-semibold mb-2': tier === 1,
        'text-base font-semibold mb-2': !tier
    }
})

const userLocation = computed(() => {
    // Check route query first, then localStorage
    if (route.query.lat && route.query.lng) {
        return {
            latitude: parseFloat(route.query.lat),
            longitude: parseFloat(route.query.lng)
        }
    } else if (typeof localStorage !== 'undefined') {
        const savedLocation = localStorage.getItem('user_location')
        if (savedLocation) {
            try {
                const location = JSON.parse(savedLocation)
                return {
                    latitude: location.latitude,
                    longitude: location.longitude
                }
            } catch (e) {
                // Ignore parsing errors
            }
        }
    }
    return null
})

const calculatedDistance = computed(() => {
    if (!userLocation.value || !props.serviceProvider.latlng) return 0
    
    return getDistanceFromLatLonInMiles(
        props.serviceProvider.latlng[0],
        props.serviceProvider.latlng[1],
        userLocation.value.latitude,
        userLocation.value.longitude
    )
})

const mapCenter = computed(() => {
    if (userLocation.value && props.serviceProvider.latlng) {
        const spLat = props.serviceProvider.latlng[0]
        const spLng = props.serviceProvider.latlng[1]
        return [(userLocation.value.latitude + spLat) / 2, (userLocation.value.longitude + spLng) / 2]
    }
    return props.serviceProvider.latlng
})
</script>

<template>
    <div class="w-full break-inside-avoid" :data-sp-tier="serviceProvider.sponsored_scope_tier === 2 ? 'Gold' : serviceProvider.sponsored_scope_tier === 1 ? 'Bronze' : 'Free'">
        <ServiceProviderCardBase 
            :includeSponsored="false"
            :sponsoredTier="0"
            class="md:sponsored-enabled">
            <div class="flex flex-row w-full pill-container" :class="pillClasses">
                <!-- Hero Image (1/4 of the pill) for bronze/gold tiers -->
                <div v-if="!compact && serviceProvider.sponsored_scope_tier > 0 && serviceProvider.hero_image_url" 
                     class="hidden lg:block w-1/4 h-full shrink-0 overflow-hidden rounded-l-lg relative z-10">
                    <img :src="serviceProvider.hero_image_url" 
                         :alt="serviceProvider.name + ' hero image'" 
                         class="h-full w-full object-cover">
                    <!-- Gradient overlay -->
                    <div class="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
                </div>
                
                <!-- Map Section (1/4 of the pill) for bronze/gold without hero image -->
                <div v-else-if="!compact && serviceProvider.sponsored_scope_tier > 0" class="hidden lg:block w-1/4 h-full shrink-0 overflow-hidden rounded-l-lg relative z-10">
                    <Map v-if="serviceProvider.latlng && serviceProvider.latlng[0] && serviceProvider.latlng[1]" :center-override="serviceProvider.latlng" :default-zoom="15" :show-center-marker="true" :show-user-location="false" :show-connection-line="false" :disable-interactions="true" class="h-full w-full object-cover"></Map>
                    <div v-else class="h-full w-full bg-gray-100 flex items-center justify-center">
                        <span class="text-[8px] text-gray-400">No location</span>
                    </div>
                </div>
                
                <!-- Content Section -->
                <div class="flex flex-col flex-1 px-4 py-4 min-w-0 h-full">
                    <h4 :class="[titleClasses, {
                        'pill-gold': serviceProvider.sponsored_scope_tier === 2,
                        'pill-bronze': serviceProvider.sponsored_scope_tier === 1,
                        'pill-free': !serviceProvider.sponsored_scope_tier
                    }]" class="break-words pill-title">{{ serviceProvider.name }}</h4>
                    <!-- Service Provider Info Content -->
                    <div class="w-full flex flex-col grow">
                        <div class="mb-2">
                            <p v-if="calculatedDistance > 0">
                                {{ t('service_providers.service_provider_card.within') }}
                                <span class="font-semibold">
                                    {{ t('service_providers.service_provider_card.within_miles', { distance: calculatedDistance.toFixed(1) }, calculatedDistance !== 1 ? 2 : 1) }}
                                </span>
                            </p>
                            <p v-if="serviceProvider.address">{{ serviceProvider.address }}</p>
                        </div>

                        <div class="mb-4 w-full hidden md:block overflow-hidden" :class="{
                            'text-xs': serviceProvider.sponsored_scope_tier === 1
                        }" v-if="!compact && serviceProvider.description && serviceProvider.sponsored_scope_tier === 2">
                            <p class="line-clamp-2">
                                {{ serviceProvider.description }}
                            </p>
                        </div>

                        <div class="grow"></div>

                        <div class="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
                            <DealerType :serviceProvider="serviceProvider" class="flex justify-between items-center"/>

                            <RouterLink
                                :to="{
                                    name: 'service_provider.profile',
                                    params: { id: serviceProvider.id },
                                    query: {
                                        lat: route.query.lat,
                                        lng: route.query.lng
                                    }
                                }"
                                class="rounded-full bg-[#b05b34] text-white py-2 px-6 hover:opacity-90 text-center">
                                <span class="focus:outline-none text-sm font-normal">
                                    {{ t('service_providers.service_provider_card.more_info') }}
                                </span>
                            </RouterLink>
                        </div>
                    </div>
                </div>
            </div>
        </ServiceProviderCardBase>
    </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/service_providers/service_providers_card_default';

@media (min-width: 768px) {
  .md\:sponsored-enabled {
    .card {
      background: v-bind('serviceProvider.sponsored_scope_tier === 2 ? "#fbbf24" : serviceProvider.sponsored_scope_tier === 1 ? "#9ca3af" : "#f9fafb"');
    }
  }
}

.pill-container {
  @apply h-64 md:h-auto;
  
  &.pill-gold:not(.pill-compact) {
    @apply md:h-56;
  }
  
  &.pill-bronze:not(.pill-compact) {
    @apply md:h-48;
  }
  
  &.pill-free,
  &.pill-compact {
    @apply md:h-40;
    filter: saturate(0.6);
  }
}

.pill-title {
  &.pill-gold {
    @apply text-amber-900;
  }
  
  &.pill-bronze {
    @apply text-gray-800;
  }
  
  &.pill-free {
    @apply text-gray-600;
  }
}


</style>